import { isAbsolute, relative, resolve, sep } from "node:path";
import { portableSpawn, type PortableSubprocess } from "@skaile/workspaces/core";
import type { BindingOrigin } from "./contract/expression.js";
import { deterministicNodeKindLabel } from "./engine/node-shape.js";
import type { FlowNode } from "./engine/types.js";

/** @deprecated Use {@link DeterministicFlowNode}; `NodeExecutorRequest.node` widened to include `check`. */
export type FunctionFlowNode = FlowNode & {
  run: Extract<NonNullable<FlowNode["run"]>, { kind: "function" }>;
};

/** Strict v2 function or check node — the two kinds NodeExecutor runs outside an agent turn. */
export type DeterministicFlowNode = FlowNode & {
  run: Extract<NonNullable<FlowNode["run"]>, { kind: "function" | "check" }>;
};

/** Resolved execution request handed across the flow engine's only I/O seam. */
export interface NodeExecutorRequest {
  /** Available strict v2 function or check node selected by the pure engine. */
  node: DeterministicFlowNode;
  /** Binding values already resolved at the node boundary. */
  inputs: Record<string, unknown>;
  /** Provenance retained separately from the values serialized for execution. */
  inputOrigins: Record<string, BindingOrigin[]>;
}

/** Captured result of one deterministic node process. */
export interface NodeExecutorResult {
  /** Process exit status, matched against the node's declared successful statuses. */
  exitStatus: number;
  /** Complete UTF-8 standard output from the process. */
  stdout: string;
  /** Complete UTF-8 standard error from the process. */
  stderr: string;
}

/** Ambient per-call controls, kept off {@link NodeExecutorRequest} because they are not execution data. */
export interface NodeExecutorOptions {
  /** Fires once the caller has abandoned this result — the work should stop. */
  signal?: AbortSignal;
}

/** Runtime seam for node kinds that execute outside an agent turn. */
export interface NodeExecutor {
  /**
   * Executes one available deterministic node with already-resolved inputs.
   *
   * `options.signal` fires once the caller has abandoned the result; an
   * implementation that honors it should stop the work and reject only after it
   * has stopped. Ignoring it is supported — an executor declaring only
   * `(request)` still satisfies this interface and behaves exactly as before,
   * forgoing only the reaping of work whose result is already discarded.
   */
  execute(request: NodeExecutorRequest, options?: NodeExecutorOptions): Promise<NodeExecutorResult>;
}

/** Session workspace required to keep deterministic execution inside the active run. */
export interface SessionNodeExecutorOptions {
  /** Root of the already-materialized session workspace. */
  workspaceDir: string;
}

function stableJsonStringify(value: unknown): string | undefined {
  return JSON.stringify(value, (_key, nested) => {
    if (nested === null || Array.isArray(nested) || typeof nested !== "object") return nested;
    return Object.fromEntries(
      Object.entries(nested as Record<string, unknown>).sort(([left], [right]) => {
        if (left < right) return -1;
        if (left > right) return 1;
        return 0;
      }),
    );
  });
}

function serializeEnvironmentInput(name: string, value: unknown, nodeLabel: string): string {
  if (typeof value === "string") return value;
  const serialized = stableJsonStringify(value);
  if (serialized === undefined) {
    throw new Error(`${nodeLabel} input ${name} is not JSON-serializable`);
  }
  return serialized;
}

function commandFor(node: DeterministicFlowNode): string[] {
  switch (node.run.runtime ?? "shell") {
    case "shell":
      return ["sh", "-c", node.run.command];
    case "node":
      return ["node", "-e", node.run.command];
    case "python":
      return ["python3", "-c", node.run.command];
  }
}

/**
 * Grace between the abort's `SIGTERM` and the `SIGKILL` that makes it true. A
 * command that traps or ignores `SIGTERM` would otherwise keep running while
 * the node reads as aborted. Exported so a caller sequencing work behind an
 * abort can outlast the escalation instead of guessing at it.
 */
export const ABORT_SIGKILL_GRACE_MS = 2_000;

function abortError(nodeLabel: string): Error {
  const error = new Error(`${nodeLabel} was aborted`);
  error.name = "AbortError";
  return error;
}

/**
 * Detached children still running. Detaching is what makes the abort a
 * process-*group* kill, but it also takes the child out of the runtime's group,
 * so a group-directed shutdown no longer reaches it — hence this sweep.
 */
const liveDetachedChildren = new Set<PortableSubprocess>();
let shutdownSweepInstalled = false;

function sweepOnShutdown(child: PortableSubprocess): void {
  liveDetachedChildren.add(child);
  void child.exited.then(() => liveDetachedChildren.delete(child));
  if (shutdownSweepInstalled) return;
  shutdownSweepInstalled = true;
  globalThis.process.once("exit", () => {
    for (const live of liveDetachedChildren) {
      try {
        live.kill("SIGKILL");
      } catch {
        /* already gone, or the group outlived our permission to signal it */
      }
    }
  });
}

/** Runs deterministic nodes inside the existing session workspace and environment. */
export class SessionNodeExecutor implements NodeExecutor {
  private readonly workspaceDir: string;

  /** Creates an executor scoped to one already-materialized session workspace. */
  constructor(options: SessionNodeExecutorOptions) {
    this.workspaceDir = resolve(options.workspaceDir);
  }

  /** Runs one function or check node process with session environment and binding overrides. */
  async execute(
    request: NodeExecutorRequest,
    options?: NodeExecutorOptions,
  ): Promise<NodeExecutorResult> {
    const nodeLabel = `${deterministicNodeKindLabel(request.node.run.kind)} node ${request.node.id}`;
    const cwd = resolve(this.workspaceDir, request.node.run.cwd ?? ".");
    const workspaceRelative = relative(this.workspaceDir, cwd);
    if (
      workspaceRelative === ".." ||
      workspaceRelative.startsWith(`..${sep}`) ||
      isAbsolute(workspaceRelative)
    ) {
      throw new Error(`${nodeLabel} cwd escapes the session workspace: ${request.node.run.cwd}`);
    }

    const boundEnvironment = Object.fromEntries(
      Object.entries(request.inputs).map(([name, value]) => [
        name,
        serializeEnvironmentInput(name, value, nodeLabel),
      ]),
    );
    const signal = options?.signal;
    if (signal?.aborted) throw abortError(nodeLabel);

    // Detached so `portableSpawn`'s kill is a process-*group* kill: a command
    // that forks children of its own — a shell script, a build tool — would
    // otherwise leave them behind. `sweepOnShutdown` pays for the detachment.
    const process = portableSpawn(commandFor(request.node), {
      cwd,
      env: { ...globalThis.process.env, ...boundEnvironment },
      detached: true,
    });
    sweepOnShutdown(process);

    let aborted = false;
    let escalation: ReturnType<typeof setTimeout> | undefined;
    const onAbort = () => {
      aborted = true;
      process.kill("SIGTERM");
      escalation = setTimeout(() => process.kill("SIGKILL"), ABORT_SIGKILL_GRACE_MS);
      escalation.unref?.();
    };
    signal?.addEventListener("abort", onAbort, { once: true });
    try {
      // Awaited even when aborted — settling is then observable proof the
      // process is gone, not proof `kill` was called. Reading both pipes to the
      // end also lets them close instead of stalling a child on a full buffer.
      const [stdout, stderr, exitStatus] = await Promise.all([
        new Response(process.stdout).text(),
        new Response(process.stderr).text(),
        process.exited,
      ]);
      if (aborted) throw abortError(nodeLabel);
      return { exitStatus, stdout, stderr };
    } finally {
      if (escalation) clearTimeout(escalation);
      signal?.removeEventListener("abort", onAbort);
    }
  }
}
